import { css, Global } from '@emotion/react';
import { MicrotaskProgress } from '@sketch-note/design-system/src/components';
import { ViewTemplateProps } from '@sketchnote/legal-templates';
import { LoaderXs } from '@sketchnote/shared-components';
import { PlateProvider } from '@udecode/plate';
import cn from 'classnames';
import { collaborativeWsUrl } from 'config';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useDispatch } from 'react-redux';
import { API } from 'src/api';
import { BlockEditor } from 'src/components/BlockEditor';
import { useLoaderStore } from 'src/components/loaders';
import { CollaborativeEditor, CollaborativeEditorProps } from 'src/features/collaborative-editor';
import { useFileAccess } from 'src/features/files/hooks/useFileAccess';
import { useSetStateBlockEditorValues } from 'src/shared/hooks';
import { useAuth } from 'src/shared/hooks/use-auth';
import { CLOSE_SIDEBAR, OPEN_SIDEBAR } from 'src/store/types';

import { useDocStore } from '../stores/useDocStore';
import { TableOfContents } from './TableOfContents';

export const LegalTemplateEditor = dynamic<ViewTemplateProps>(() =>
  import('@sketchnote/legal-templates').then((mod) => mod.ViewTemplate)
);

export const DocPage = ({ docId }: { docId: string }): JSX.Element => {
  const router = useRouter();
  const dispatch = useDispatch();
  const wsToken = process.env.WS_TOKEN;
  const [microtasks, setMicrotasks] = useState({ total: 0, completed: 0 });
  const { userDetails, isPublicFile } = useAuth((s) => ({
    userDetails: s.userDetails,
    isPublicFile: s.isPublicFile,
  }));
  const fetchDoc = useDocStore((s) => s.fetchDoc);
  const fetchDocument = useDocStore((s) => s.fetchDocument);
  useSetStateBlockEditorValues({ docId: docId });
  const templateId = router.query.templateId as string;

  const { setIsLoading } = useLoaderStore();
  const docData = useDocStore(
    useCallback(
      (s) => {
        if (typeof docId === 'string') {
          return s.docs[docId];
        }
      },
      [docId]
    )
  );

  const { isReadOnly } = useFileAccess();

  useEffect(() => {
    if (templateId) {
      fetchDoc(docId as string, true);
    } else {
      fetchDocument(docId as string);
    }
  }, [docId, templateId]);

  const handleChangeMicrotasks: CollaborativeEditorProps['onChangeMicroTasks'] = useCallback(
    ({ total, completed }) => {
      setMicrotasks({ total, completed });
    },
    []
  );

  useEffect(() => {
    if (docData?.data?.type === 'workflow') {
      dispatch({ type: CLOSE_SIDEBAR });
    }
    return () => {
      dispatch({ type: OPEN_SIDEBAR });
    };
  }, [docData?.data?.type]);

  const handleGenerateLegalToCreation = async ({ doc }) => {
    setIsLoading(true);
    try {
      const response = await API.post('files/legal/create', {
        document: doc,
        creationId: docId,
      });
      if (response.data?.success) {
        fetchDoc(docId as string);
      }
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };
  const lessonSlug = router?.query?.slug;

  return (
    <div>
      <div
        className={cn({
          'bg-gray-100': docData?.data?.type === 'workflow',
        })}
      >
        {docData?.data?.type !== 'workflow' && <TableOfContents />}
        <DndProvider backend={HTML5Backend}>
          <div>
            {!docData?.data && docData?.isFetching ? (
              <div className="flex items-center justify-center m-auto py-50">
                <div className="overflow-hidden">
                  <LoaderXs className="mx-auto text-xss" />
                </div>
              </div>
            ) : docData?.data?.type === 'workflow' ? (
              <LegalTemplateEditor
                onGenerateDoc={handleGenerateLegalToCreation}
                content={docData?.data?.document}
                inputs={docData?.data?.questions}
              />
            ) : (
              <div className={cn(' mx-auto  w-full relative h-full px-48 pt-16')}>
                <div className="w-full px-48 max-w-864 m-auto">
                  {microtasks.total > 0 ? (
                    <MicrotaskProgress current={microtasks.completed} total={microtasks.total} />
                  ) : null}
                </div>

                {docData?.data &&
                userDetails?.memberInformation.memberId &&
                docData?.data.creationId &&
                !templateId ? (
                  <CollaborativeEditor
                    lesson={lessonSlug as string}
                    creationId={docId}
                    onChangeMicroTasks={handleChangeMicrotasks}
                    document={docData?.data.document}
                    title={docData?.data.title}
                    readOnly={isReadOnly}
                    token={wsToken || ''}
                  />
                ) : (
                  <div className="max-w-864 px-48">
                    <BlockEditor isReadOnly value={docData?.data?.document} />
                  </div>
                )}
              </div>
            )}
          </div>
        </DndProvider>
      </div>
      <Global
        styles={css`
          body {
            overflow: hidden;
          }
          html,
          body {
            overscroll-behavior: none;
          }
        `}
      />
    </div>
  );
};
